import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { baseUrl } from '../../hooks/useEndpoints';
import { sportTaxonomiesEndpoints } from '../../endpoints/trainingStatistics';
import { SPACE_TYPES } from '../../enums/Space';
import { ITeam } from '../../models/Team';
import { ISpace } from '../../models/Space';
import { ISportTaxonomy } from '../../models/trainingStatistics';

/**
 * Mappa SPACE_TYPES → sport key tassonomia didattica backend.
 * Gym e Athletics non hanno tassonomia campo → null.
 */
const SPACE_TO_SPORT: Record<string, string | null> = {
  [SPACE_TYPES.SOCCER]: 'soccer',
  [SPACE_TYPES.RUGBY_UNION]: 'rugby_union',
  [SPACE_TYPES.BASKET]: 'basketball',
  [SPACE_TYPES.VOLLER_BALL]: 'volleyball',
  [SPACE_TYPES.ATHLETICS]: 'athletics',
};

/**
 * Risolve lo sport del team e carica la tassonomia didattica associata.
 * Usato per passare sport+taxonomy ai blocchi per la classificazione didattica.
 * Spec v1.1.1 sez 20: classificazione filtrata per sport della squadra corrente.
 */
export function useSessionTaxonomy(teamId: string | undefined) {
  const teamQ = useQuery({
    queryKey: ['team', teamId],
    queryFn: () =>
      axios.get<ITeam>(`${baseUrl}/teams/${teamId}`).then((r) => r.data),
    enabled: !!teamId,
    staleTime: 5 * 60_000,
  });

  // team.spaceId può arrivare come stringa (id) o come oggetto popolato (varia
  // per endpoint backend). Normalizziamo: se popolato, prendiamo type
  // direttamente (no fetch); se stringa, scarichiamo lo space.
  const rawSpaceId = teamQ.data?.spaceId as unknown;
  const isPopulatedSpace =
    !!rawSpaceId &&
    typeof rawSpaceId === 'object' &&
    'type' in (rawSpaceId as object);
  const spaceIdString =
    typeof rawSpaceId === 'string'
      ? rawSpaceId
      : isPopulatedSpace && '_id' in (rawSpaceId as object)
        ? String((rawSpaceId as { _id: unknown })._id)
        : undefined;

  const spaceQ = useQuery({
    queryKey: ['space', spaceIdString],
    queryFn: () =>
      axios.get<ISpace>(`${baseUrl}/spaces/${spaceIdString}`).then((r) => r.data),
    enabled: !!spaceIdString && !isPopulatedSpace,
    staleTime: 10 * 60_000,
  });

  const spaceType = (
    isPopulatedSpace
      ? (rawSpaceId as { type?: string }).type
      : spaceQ.data?.type
  ) as string | undefined;
  const sport = spaceType ? SPACE_TO_SPORT[spaceType] ?? null : null;

  const taxonomyQ = useQuery({
    queryKey: ['sportTaxonomy', sport],
    queryFn: async () => {
      if (!sport) return null;
      const list = await sportTaxonomiesEndpoints.getBySport(sport, 'it');
      return (list[0] as ISportTaxonomy | undefined) ?? null;
    },
    enabled: !!sport,
    staleTime: 30 * 60_000,
  });

  return {
    sport,
    taxonomy: taxonomyQ.data ?? null,
    isLoading: teamQ.isLoading || spaceQ.isLoading || taxonomyQ.isLoading,
  };
}
